import { useEffect, useState } from "react";
import { Copy, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import Modal from "./Modal";

export default function CareCircleModal({ profile, onClose }) {
  const [data, setData] = useState(null);
  const [lastCode, setLastCode] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/care-circle", { params: { profile_id: profile.id } });
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const invite = async () => {
    try {
      const { data: d } = await api.post("/care-circle/invite", { profile_id: profile.id });
      setLastCode(d.code);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/login?circle=${lastCode}`);
      toast.success("Invite link copied — send it to the parent, teacher or therapist");
    } catch {
      toast.error("Could not copy — share the code instead");
    }
  };

  const remove = async (membershipId) => {
    try {
      await api.delete(`/care-circle/${membershipId}`);
      await load();
      toast.success("Member removed");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal title={`Care circle — ${profile.name}`} onClose={onClose} testId="care-circle-modal">
      <p className="text-base text-muted-foreground font-semibold">
        Invite a parent, teacher or therapist. They'll see and update {profile.name}'s routines, boards and notes from their own account.
      </p>

      {lastCode && (
        <div data-testid="circle-invite-display" className="mt-4 bg-[#ECC94B]/20 border-2 border-[#ECC94B] rounded-2xl p-4 flex flex-wrap items-center gap-3">
          <p className="font-bold">Code: <span className="font-extrabold tracking-widest">{lastCode}</span></p>
          <button data-testid="circle-copy-link" onClick={copyLink} className="min-h-[48px] px-4 rounded-full bg-white border-2 border-foreground/25 font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
            <Copy size={18} strokeWidth={2.5} /> Copy invite link
          </button>
        </div>
      )}

      <button data-testid="circle-invite-button" onClick={invite} className="mt-4 w-full min-h-[56px] rounded-full bg-primary text-white font-bold flex items-center justify-center gap-2">
        <Plus size={22} strokeWidth={3} /> Invite someone
      </button>

      <div className="mt-6 space-y-3">
        {data?.members?.length === 0 && <p className="text-muted-foreground font-semibold">No one else in the circle yet.</p>}
        {data?.members?.map((m) => (
          <div key={m.membership_id} data-testid={`circle-member-${m.membership_id}`} className="flex items-center gap-3 border-2 border-foreground/10 rounded-2xl p-3">
            <span className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center font-extrabold text-primary shrink-0">{m.name?.[0]?.toUpperCase()}</span>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{m.name}</p>
              <p className="text-sm font-semibold text-muted-foreground truncate">{m.email}</p>
            </div>
            <button data-testid={`circle-remove-${m.membership_id}`} onClick={() => remove(m.membership_id)} aria-label={`Remove ${m.name}`} className="min-w-[44px] min-h-[44px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200">
              <Trash2 size={18} strokeWidth={2.5} />
            </button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
